import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import SecureLS from 'secure-ls'
import { MAX_PERSISTED_USER_JOURNEYS } from '@/store/modules/journey'

let ls = new SecureLS({ isCompression: false })

const PERSIST_DEBOUNCE_MS = 250
let persistTimeout = null
let pendingPersist = null

const flushPendingPersist = () => {
  if (!pendingPersist) return
  ls.set(pendingPersist.key, pendingPersist.value)
  pendingPersist = null
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPendingPersist)
}

// Modules
import auth from '@/store/modules/auth';
import tenant from '@/store/modules/tenant';
import loading from '@/store/modules/loading';
import users from '@/store/modules/users';
import journey from '@/store/modules/journey';
import journeyCategory from "@/store/modules/journeyCategory";
import notifications from '@/store/modules/notifications';
import layout from "@/store/modules/layout";
import file from "@/store/modules/file";
import treasureChest from "@/store/modules/treasureChest";
import reusableAssets from "@/store/modules/reusableAssets";
import userNotifications from "@/store/modules/userNotifications";
import userPreferences from "@/store/modules/userPreferences";
import journeyReusableTemplates from "@/store/modules/journeyReusableTemplates";
import internalCurrency from "@/store/modules/internalCurrency";
import tenantSpecificAssets from "@/store/modules/tenantSpecificAssets";
import pushNotifications from "@/store/modules/pushNotifications";
import cmv from "@/store/modules/cmv";
import offline from "@/store/modules/offline";
import chat from "@/store/modules/chat";
import ds24 from "@/store/modules/ds24";
import video from "@/store/modules/video";
import uiI18n from "@/store/modules/uiI18n";
import emailTemplates from "@/store/modules/emailTemplates";

export default createStore({
  modules: {
    loading,
    auth,
    tenant,
    users,
    journey,
    journeyCategory,
    notifications,
    layout,
    file,
    treasureChest,
    reusableAssets,
    userNotifications,
    userPreferences,
    journeyReusableTemplates,
    internalCurrency,
    tenantSpecificAssets,
    pushNotifications,
    cmv,
    offline,
    chat,
    ds24,
    video,
    uiI18n,
    emailTemplates,
  },
  actions: {
    resetAllStores({ commit }) {
      commit('cmv/RESET_STATE');
      commit('file/RESET_STATE');
      commit('internalCurrency/RESET_STATE');
      commit('journey/RESET_STATE');
      commit('journeyCategory/RESET_STATE');
      commit('journeyReusableTemplates/RESET_STATE');
      commit('layout/RESET_STATE');
      commit('reusableAssets/RESET_STATE');
      //commit('tenant/RESET_STATE');
      commit('tenantSpecificAssets/RESET_STATE');
      commit('treasureChest/RESET_STATE');
      commit('userPreferences/RESET_STATE');
      commit('uiI18n/RESET_STATE');
      commit('users/RESET_STATE');
      commit('pushNotifications/RESET_STATE');
      commit('chat/RESET_STATE');
      // commit RESET_STATE for other modules as needed
    },
  },
  plugins: [
    createPersistedState({
      key: 'commovis',
      reducer: (state) => ({
        tenant: { tenantinfo: state.tenant.tenantinfo },
        auth: {
          userInfo: state.auth.userInfo,
          devicelist: state.auth.devicelist,
        },
        userPreferences: {
          userPreferences: state.userPreferences.userPreferences,
          activeFolderPath: state.userPreferences.activeFolderPath,
        },
        journey: {
          userJourneys: state.journey.userJourneys.slice(0, MAX_PERSISTED_USER_JOURNEYS),
          lastUserJourney: state.journey.lastUserJourney,
          isParticipantView: state.journey.isParticipantView,
        },
        treasureChest: { treasureChestAssets: state.treasureChest.treasureChestAssets },
        userNotifications: { userNotifications: state.userNotifications.userNotifications },
        journeyReusableTemplates: {
          journeyReusableTemplates: state.journeyReusableTemplates.journeyReusableTemplates,
          journeyTemplates: state.journeyReusableTemplates.journeyTemplates,
        },
        tenantSpecificAssets: {
          MemoryCards: state.tenantSpecificAssets.MemoryCards,
          ExpeditionLogs: state.tenantSpecificAssets.ExpeditionLogs,
        },
        chat: {
          LinkedUsersPerJourney: state.chat.LinkedUsersPerJourney,
          UsersLinkedToCurrentUser: state.chat.UsersLinkedToCurrentUser,
          Rooms: state.chat.Rooms,
          MessagesByRoom: Object.fromEntries(
            Object.entries(state.chat.MessagesByRoom).map(([roomID, messages]) => [
              roomID,
              messages.slice(-50),
            ])
          ),
          HasMoreMessagesByRoom: state.chat.HasMoreMessagesByRoom,
        },
        ds24: {
          sessionToken: state.ds24.sessionToken,
          sessionJourneyId: state.ds24.sessionJourneyId,
        }
      }),
      storage: {
        getItem: (key) => ls.get(key),
        setItem: (key, value) => {
          pendingPersist = { key, value }
          clearTimeout(persistTimeout)
          persistTimeout = setTimeout(flushPendingPersist, PERSIST_DEBOUNCE_MS)
        },
        removeItem: (key) => {
          clearTimeout(persistTimeout)
          pendingPersist = null
          ls.remove(key)
        },
      },
    }),
  ],
})
